// Main.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useNodePeek } from 'context/NodePeekContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Routing
import { Outlet, useLocation } from 'react-router-dom';

// Utilities
import { defaultFont } from 'utilities/Defaults';
import { SectionType } from 'utilities/Enums';
import { loadFont } from 'utilities/Fonts';
import { useIsMobile } from 'hooks/useDeviceDetect';
import { inferSectionFromPath } from 'utilities/Routing';

// CSS
import './Main.css';

// Components
import Background from 'components/background/Background';
import SaveIndicator from 'components/saveindicator/SaveIndicator';

// Menus
import Sidebar from 'main/sidebar/Sidebar';
import MobileMenu from 'main/menu/mobile/MobileMenu';

// Hooks
import usePreloadNodeEngagements from 'hooks/usePreloadNodeEngagements';
import usePreloadIcons from 'hooks/usePreloadIcons';
import { updateSetting } from 'services/settings/SettingWriteService';

// Dynamic Styles
import { MainStyles } from './MainStyles';
import DrawerButton from './drawerbutton/DrawerButton';

/**
 * Main
 * 
 * Renders the main application component, including toolbar, menus, and content.
 * 
 * Responsively adapts layout for desktop, pad, and mobile views.
 * 
 * @returns {ReactNode} Main component.
 */
const Main = () => {

  const DEFAULT_DESKTOP_MENU_WIDTH = 290;
  const MIN_DESKTOP_MENU_WIDTH = 200;
  const MAX_DESKTOP_MENU_WIDTH = 420;

  // ----- CONTEXT ----- //

  const { currentSection, setCurrentSection } = useNavigation();
  const { clearPeek, isPeekOpen } = useNodePeek();

  // User Context
  const {
    user,
    userSettings,
    setUserSettings,
  } = useUser();

  // Workspace Context
  const { activeWorkspace } = useWorkspace();

  // Theme Context
  const { theme } = useTheme();

  // UI Context
  const { background } = useUI();

  usePreloadNodeEngagements();
  usePreloadIcons();

  // ----- CONSTANTS ----- //

  const isMobile = useIsMobile();

  const activeWorkspaceKey = activeWorkspace?.key || userSettings?.ACTIVE_WORKSPACE_KEY || null;

  const getClampedDesktopMenuWidth = useCallback((value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return DEFAULT_DESKTOP_MENU_WIDTH;
    return Math.min(MAX_DESKTOP_MENU_WIDTH, Math.max(MIN_DESKTOP_MENU_WIDTH, Math.round(numericValue)));
  }, []);

  const desktopMenuWidthFromSettings = useMemo(() => {
    if (!activeWorkspaceKey) return DEFAULT_DESKTOP_MENU_WIDTH;

    const widthMap = userSettings?.DESKTOP_MENU_WIDTH_BY_WORKSPACE;
    const workspaceWidth = widthMap && typeof widthMap === 'object'
      ? widthMap[activeWorkspaceKey]
      : null;

    return getClampedDesktopMenuWidth(workspaceWidth);
  }, [activeWorkspaceKey, getClampedDesktopMenuWidth, userSettings?.DESKTOP_MENU_WIDTH_BY_WORKSPACE]);

  const [desktopMenuWidth, setDesktopMenuWidth] = useState(desktopMenuWidthFromSettings);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isResizeHandleHovered, setIsResizeHandleHovered] = useState(false);
  const isResizingRef = useRef(false);
  const lastRouteSignatureRef = useRef('');
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_DESKTOP_MENU_WIDTH);
  const latestDesktopMenuWidthRef = useRef(desktopMenuWidthFromSettings);

  useEffect(() => {
    setDesktopMenuWidth(desktopMenuWidthFromSettings);
  }, [desktopMenuWidthFromSettings]);

  useEffect(() => {
    latestDesktopMenuWidthRef.current = desktopMenuWidth;
  }, [desktopMenuWidth]);

  const persistDesktopMenuWidth = useCallback(async (nextWidth) => {
    if (!user?.key || !activeWorkspaceKey) return;

    const widthMap = {
      ...(userSettings?.DESKTOP_MENU_WIDTH_BY_WORKSPACE || {}),
      [activeWorkspaceKey]: getClampedDesktopMenuWidth(nextWidth),
    };

    try {
      await updateSetting(user.key, {
        DESKTOP_MENU_WIDTH_BY_WORKSPACE: widthMap,
      });

      setUserSettings && setUserSettings({
        ...(userSettings || {}),
        DESKTOP_MENU_WIDTH_BY_WORKSPACE: widthMap,
        userKey: user.key,
      });
    } catch (error) {
      console.error('[Main] failed to persist desktop menu width:', error);
    }
  }, [activeWorkspaceKey, getClampedDesktopMenuWidth, setUserSettings, user?.key, userSettings]);

  const handleResizeMouseMove = useCallback((event) => {
    if (!isResizingRef.current) return;

    const deltaX = event.clientX - resizeStartXRef.current;
    const nextWidth = getClampedDesktopMenuWidth(resizeStartWidthRef.current + deltaX);
    setDesktopMenuWidth(nextWidth);
  }, [getClampedDesktopMenuWidth]);

  const handleResizeMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleResizeMouseMove);
    window.removeEventListener('mouseup', handleResizeMouseUp);

    const nextWidth = latestDesktopMenuWidthRef.current;
    if (nextWidth !== desktopMenuWidthFromSettings) {
      persistDesktopMenuWidth(nextWidth);
    }
  }, [desktopMenuWidthFromSettings, handleResizeMouseMove, persistDesktopMenuWidth]);

  const handleResizeMouseDown = useCallback((event) => {
    if (isMobile) return;

    event.preventDefault();

    isResizingRef.current = true;
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = latestDesktopMenuWidthRef.current;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleResizeMouseMove);
    window.addEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp, isMobile]);

  useEffect(() => () => {
    window.removeEventListener('mousemove', handleResizeMouseMove);
    window.removeEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  // ----- RENDER CONTENT ----- //

  // Routing
  const location = useLocation();

  useEffect(() => {
    if (!isMobile && isMobileDrawerOpen) {
      setIsMobileDrawerOpen(false);
    }
  }, [isMobile, isMobileDrawerOpen]);

  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const routeSignature = `${location.pathname}${location.search}${location.hash}`;

    if (!lastRouteSignatureRef.current) {
      lastRouteSignatureRef.current = routeSignature;
      return;
    }

    if (lastRouteSignatureRef.current === routeSignature) {
      return;
    }

    lastRouteSignatureRef.current = routeSignature;

    if (isMobile && isPeekOpen) {
      clearPeek();
    }
  }, [clearPeek, isMobile, isPeekOpen, location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    if (!isMobileDrawerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileDrawerOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    const inferredSection = inferSectionFromPath(location.pathname);
    if (inferredSection && inferredSection !== currentSection) {
      setCurrentSection(inferredSection);
      return;
    }

    if (!inferredSection && location.pathname === '/' && currentSection !== SectionType.HOME) {
      setCurrentSection(SectionType.HOME);
    }
  }, [currentSection, location.pathname, setCurrentSection]);

  useEffect(() => {
    if (!user) return;
    loadFont(user.fontFamily || defaultFont);
  }, [user]);

  return (
    <>
      {/* DYNAMIC STYLES */}
      <MainStyles
        fontFamily={defaultFont}
        selectionBackground={theme.selectionBackground}
        selectionForeground={theme.selectionForeground}
      />

      {/* CONTAINER */}
      <div className="main-container"
        style={{
          backgroundColor: isMobile ? theme.backgroundMobile : theme.background
        }}>

        <Background node={background || null} />

        <div className="main-content-layer">

          {/* DESKTOP MENU */}
          {!isMobile && (
            <>
              {activeWorkspace ? (
                <div className="main-desktop-menu"
                  style={{
                    width: desktopMenuWidth
                  }}>

                  <Sidebar
                    coverHeight={desktopMenuWidth * 0.75}
                  />

                  <div
                    className="main-desktop-menu-resize-handle"
                    onMouseDown={handleResizeMouseDown}
                    onMouseEnter={() => setIsResizeHandleHovered(true)}
                    onMouseLeave={() => setIsResizeHandleHovered(false)}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize desktop menu"
                  >
                    <div
                      className="main-desktop-menu-resize-handle-track"
                      style={{
                        backgroundColor: isResizeHandleHovered ? theme.backgroundHover : "transparent",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="main-desktop-menu-loading"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >
                  Loading workspace...
                </div>
              )}
            </>
          )}

          {user && (
            <div className="main-right">

              {isMobile && (
                <>
                  <div
                    className={`main-mobile-drawer-overlay${isMobileDrawerOpen ? ' open' : ''}`}
                    style={{
                      backgroundColor: theme.overlay,
                    }}
                    aria-hidden={!isMobileDrawerOpen}
                    onClick={() => setIsMobileDrawerOpen(false)}
                  />

                  <aside
                    className={`main-mobile-drawer${isMobileDrawerOpen ? ' open' : ''}`}
                    style={{
                      backgroundColor: theme.background,
                    }}
                    aria-label="Sidebar menu"
                    aria-hidden={!isMobileDrawerOpen}
                  >
                    <Sidebar
                      forceVisible={true}
                      //hideAddButton={true}
                      hideAddButton={false}
                      hidePrimaryButtons={true}
                      hideTemplatesButton={true}
                      onRequestClose={() => setIsMobileDrawerOpen(false)}
                    />
                  </aside>

                  <div className="main-mobile-drawer-button-wrapper"
                    style={{
                      width: isMobileDrawerOpen ? '100%' : '50px',
                    }}>
                    <DrawerButton
                      isOpen={isMobileDrawerOpen}
                      setIsOpen={setIsMobileDrawerOpen}
                    />
                  </div>
                </>
              )}

              <div className={`main-middle${isMobile ? ' mobile-layout' : ''}`}>

                {/* CONTENT */}
                <div className={`main-content${isMobile ? ' mobile' : ''}`}>
                  <Outlet />
                </div>

              </div>

              {/* MOBILE MENU */}
              {isMobile && (
                <div className="main-mobile-menu">
                  <MobileMenu />
                </div>
              )}
            </div>
          )}

          {/* BACKGROUND SAVE/UPLOAD INDICATOR */}
          <SaveIndicator />

        </div>

      </div >
    </>
  );
};

export default Main;
